// onboardingService.js
//
// Thin async wrappers over FetchAPI for the Customer Onboarding Checklist
// endpoints (CustomerOnboardingController / CustomerOnboardingStepController /
// OnboardingChecklistTemplateController). Colocated with the tab because this
// state is single-screen; mirrors helpers/fakebackend_helper.js one-line style
// and the apiKeysService.js pattern.
//
// FetchAPI auto-attaches `Authorization: Bearer <authUser.token>` from
// localStorage and returns `undefined` on a 401 — callers must null-check.
//
// GET id handling: FetchAPI appends `/<userData.id>` (lowercase id) to the URL,
// so we pass { id } to build `/progress/5`, `/forcustomer/5` etc.
//
// POST gotcha: never put a lowercase `id` on a POST item — FetchAPI would append
// `/<id>` to the url. Our payloads use capitalised `Id` / `Customer_Id`, so the
// url is left intact and the value travels in the body.

import { FetchAPI } from "../../../../helpers/APIdata/fetchAPI";

// GET /customeronboarding/forcustomer/{Customer_Id}
// -> { status, customeronboarding_list: [...] }
export const listOnboardingForCustomer = (customerId) =>
  FetchAPI("/customeronboarding/forcustomer", "GET", { id: customerId });

// GET /customeronboarding/company
// Find-or-create and return the single COMPANY-level onboarding for the
// logged-in company (each company has its own DB). Returns the same shape as
// /progress: { status, customeronboarding_data, customeronboardingstep_list,
// onboarding_status, step_total, step_complete, percent_complete }. No body —
// the acting company comes from the JWT.
export const getCompanyOnboarding = () =>
  FetchAPI("/customeronboarding/company", "GET");

// GET /customeronboardingstepstatus/list
// The fixed status lookup: New / In Progress / Blocked / Completed / N/A.
// -> { status, customeronboardingstepstatus_list: [{ Id, Name, Slug, Order,
//      FlagResolved, Color }] }
export const listStepStatuses = () =>
  FetchAPI("/customeronboardingstepstatus/list", "GET");

// GET /customeronboarding/progress/{id}
// -> { status, customeronboarding_data, customeronboardingstep_list,
//      onboarding_status, step_total, step_complete, percent_complete }
export const getOnboardingProgress = (onboardingId) =>
  FetchAPI("/customeronboarding/progress", "GET", { id: onboardingId });

// POST /customeronboarding/start
// body: [{ Customer_Id, OnboardingChecklistTemplate_Id? }]
// -> { status, CustomerOnboarding_Id }
export const startOnboarding = (customerId, templateId) => {
  const row = { Customer_Id: customerId };
  if (templateId) row.OnboardingChecklistTemplate_Id = templateId;
  return FetchAPI("/customeronboarding/start", "POST", [row]);
};

// POST /customeronboardingstep/complete  body: [{ Id, Notes? }]
export const completeOnboardingStep = (stepId, notes) => {
  const row = { Id: stepId };
  if (notes !== undefined && notes !== null) row.Notes = notes;
  return FetchAPI("/customeronboardingstep/complete", "POST", [row]);
};

// POST /customeronboardingstep/uncomplete  body: [{ Id }]
export const uncompleteOnboardingStep = (stepId) =>
  FetchAPI("/customeronboardingstep/uncomplete", "POST", [{ Id: stepId }]);

// POST /customeronboardingstep/setstatus
// body: [{ Id, CustomerOnboardingStepStatus_Id, CapturedValue?, FollowUpOwner?,
//          FollowUpDueDate?, Notes? }]
// The chosen status comes from the CustomerOnboardingStepStatus lookup; its
// FlagResolved drives FlagComplete (Completed / N/A resolve the step) and rolls
// the parent onboarding's completion — re-fetch progress after calling so the
// derived status/percentage refresh.
export const setOnboardingStepStatus = (stepId, statusId, fields = {}) =>
  FetchAPI("/customeronboardingstep/setstatus", "POST", [
    { Id: stepId, CustomerOnboardingStepStatus_Id: statusId, ...fields },
  ]);

// PUT /customeronboardingstep/update  body: [{ Id, ...fields }]
// Used for silent autosave of free fields (CapturedValue, Notes, FollowUpOwner,
// FollowUpDueDate) that don't change the step's status.
export const updateOnboardingStep = (stepId, fields) =>
  FetchAPI("/customeronboardingstep/update", "PUT", [{ Id: stepId, ...fields }]);

// GET /onboardingchecklisttemplate/list -> { status, onboardingchecklisttemplate_list }
export const listOnboardingTemplates = () =>
  FetchAPI("/onboardingchecklisttemplate/list", "GET");

// POST /customeronboarding/importtemplate
// body: [{ CustomerOnboarding_Id, OnboardingChecklistTemplate_Id }]
// Appends the template's groups + steps into the running onboarding.
export const importTemplateIntoOnboarding = (onboardingId, templateId) =>
  FetchAPI("/customeronboarding/importtemplate", "POST", [
    {
      CustomerOnboarding_Id: onboardingId,
      OnboardingChecklistTemplate_Id: templateId,
    },
  ]);

// POST /customeronboarding/savetemplate  (developer-only)
// body: [{ CustomerOnboarding_Id, Name, Description? }]
// Writes the running onboarding's groups + steps back as a new master template.
export const saveOnboardingAsTemplate = (onboardingId, fields) =>
  FetchAPI("/customeronboarding/savetemplate", "POST", [
    { CustomerOnboarding_Id: onboardingId, ...fields },
  ]);

// ── Instance (live) group + step editing on a running onboarding ──
export const createOnboardingGroup = (fields) =>
  FetchAPI("/customeronboardinggroup/create", "POST", [fields]);

export const updateOnboardingGroup = (id, fields) =>
  FetchAPI("/customeronboardinggroup/update", "PUT", [{ Id: id, ...fields }]);

export const deleteOnboardingGroup = (id) =>
  FetchAPI("/customeronboardinggroup/delete", "DELETE", [id]);

export const createOnboardingStep = (fields) =>
  FetchAPI("/customeronboardingstep/create", "POST", [fields]);

export const deleteOnboardingStep = (id) =>
  FetchAPI("/customeronboardingstep/delete", "DELETE", [id]);

// Bulk reorder / regroup after a drag-and-drop: array of
// { Id, Order, CustomerOnboardingGroup_Id }.
export const reorderOnboardingSteps = (rows) =>
  FetchAPI("/customeronboardingstep/update", "PUT", rows);

// ── Per-step note history log ──
// GET /customeronboardingstepnote/listforonboarding/{id}
// -> { status, customeronboardingstepnote_list: [{ Id, CustomerOnboardingStep_Id,
//      Note, CreatedDateTime, CreatedStaff_FullName }] } (oldest first)
export const listOnboardingNotes = (onboardingId) =>
  FetchAPI("/customeronboardingstepnote/listforonboarding", "GET", {
    id: onboardingId,
  });

export const createStepNote = (stepId, note) =>
  FetchAPI("/customeronboardingstepnote/create", "POST", [
    { CustomerOnboardingStep_Id: stepId, Note: note },
  ]);

export const deleteStepNote = (id) =>
  FetchAPI("/customeronboardingstepnote/delete", "DELETE", [id]);
